import React, { useCallback, useEffect, useState } from 'react'
import { Button, message } from 'antd'
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'

// Custom hook to handle fullscreen functionality
const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isSupported, setIsSupported] = useState(false)

  // Check if fullscreen is supported
  useEffect(() => {
    const fullscreenSupported =
      document.documentElement.requestFullscreen ||
      document.documentElement.webkitRequestFullscreen ||
      document.documentElement.mozRequestFullScreen ||
      document.documentElement.msRequestFullscreen

    setIsSupported(!!fullscreenSupported)
  }, [])

  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen(Boolean(document.fullscreenElement))
  }, [])

  useEffect(() => {
    // Don't add listeners if on iOS
    if (isIOS) return

    // Add event listeners for fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    // Cleanup listeners on unmount
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [handleFullscreenChange])

  const toggleFullscreen = useCallback(() => {
    if (isIOS) {
      message.info('Fullscreen mode is not supported on iOS. Add this app to your home screen for a better experience.')
      return
    }

    if (!isSupported) {
      message.info('Fullscreen mode is not supported on your device.')
      return
    }

    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen()
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen()
        } else if (document.documentElement.msRequestFullscreen) {
          document.documentElement.msRequestFullscreen()
        }

        if (isAndroid) {
          // On Android, give feedback about screen orientation
          message.success('Rotate your device for the best fullscreen experience')
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error)
      message.error('Could not toggle fullscreen mode')
    }
  }, [isSupported])

  return { isFullscreen, toggleFullscreen, isSupported }
}

const FullscreenButton = ({ style }) => {
  // Don't render on iOS or if mobile and fullscreen isn't supported
  const { isFullscreen, toggleFullscreen, isSupported } = useFullscreen()

  if (isIOS || (isMobile && !isSupported)) {
    return null
  }

  return (
    <Button
      type='text'
      icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
      onClick={toggleFullscreen}
      style={{
        fontSize: '16px',
        padding: '4px 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
    />
  )
}

export default FullscreenButton
